import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { RadioGroup } from "@headlessui/react";
import { faPencil } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import Modal from "../../layouts/Modal";
import classNames from "../../utils/classNames";
import sortOffers from "../../utils/sortOffers";
import PriceFormat from "../../utils/priceFormat";
import openInNewTab from "../../utils/openInNewTab";
import { FormatType } from "../../models/EbayOffer";
import Card from "../../components/shared/Card/Card";
import usePartInvoice from "../../data/usePartInvoice";
import useSinglePart from "../../data/useSinglePart";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import useWilmaUsers from "../../data/useWilmaUsers";
import formatDateTime from "../../utils/formatDateTime";
import useContactSingle from "../../data/useContactsSingle";
import PartQc from "../../components/leads/LeadParts/PartQc";
import useLeadByVehicleId from "../../data/useLeadByVehicleId";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useInvoiceMutations from "../../data/useInvoiceMutations";
import usePartMutations from "../../data/usePartMutations";
import LocationPicker from "../../components/stock/LocationPicker";
import PartNumberEdit from "../../components/stock/PartNumberEdit";
import ContactFromId from "../../components/contacts/ContactFromId";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import AccountDetails from "../../components/accounts/AccountDetails";
import StockPrintLabels from "../../components/stock/StockPrintLabels";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import ViewOnButton from "../../components/shared/ViewOnButton";
import VehiclePartsCard from "../../components/vehicles/VehiclePartsCard";
import EbayPaymentMethod from "../../components/ebayOrders/EbayPaymentMethod";
import EbayPaymentStatus from "../../components/ebayOrders/EbayPaymentStatus";
import useNotification from "../../components/notifications/useNotifications";
import CreditInvoiceModal from "../../components/invoices/CreditInvoiceModal";
import EditListingImages from "../../components/ebayListings/EditListingImages";
import SendToQcModal from "../../components/stock/SendToQcModal";
import PartVehicleDetails from "../../components/leads/LeadParts/PartVehicleDetails";
import EbayPartMessages from "../../components/shared/EbayPartMessages/EbayPartMessages";
import { IPart, IPartCancelRequest, PartStatus } from "../../models/Part";
import WarehouseLocationFromId from "../../components/warehouseLocations/WarehouseLocationFromId";
import LinkedWorkOrdersCard from "../../components/workOrders/LinkedWorkOrdersCard";
import { SearchPaletteItem } from "../../components/shared/Inputs/SearchPaletteTypes";
import UnlistedDetailsCard from "../../components/shared/Unlisted/UnlistedDetailsCard";
import WarningBar from "../../components/shared/Card/WarningBar";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";



const StockInformation = ({ part }: { part: IPart }) => {
  const navigate = useNavigate();
  const { lead } = useLeadByVehicleId("StockInformation");
  const { cancel } = useSinglePartMutations(part.id!);
  const { invoice } = usePartInvoice(part.id!);
  const { downloadInvoice } = useInvoiceMutations();
  const { contact } = useContactSingle(part.removedBy!);
  const { addNotification } = useNotification();
  const [open, setOpen] = useState(false);
  const [partNumberOpen, setPartNumberOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [qcOpen, setQcOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);

  const { wilmaUsers } = useWilmaUsers();
  const [locatedBy, setLocatedBy] = useState<string | null>(null);

  useEffect(() => {
    if (part.locatedBy && !wilmaUsers.isLoading && wilmaUsers.data) {
      const user = wilmaUsers.data.data.find((user) => user.id === part.locatedBy);
      setLocatedBy(user ? user.fullname : "Unknown User");
    }
  }, [part.locatedBy, wilmaUsers.data, wilmaUsers.isLoading]);

  const getCancellable = () => {
    if (part.soldDate === null) return "No - Not Sold";

    if (part.shippedOn !== null) return "No - Already Shipped";

    if (
      new Date().getTime() - new Date(part.soldDate!).getTime() >
      7 * 24 * 60 * 60 * 1000
    )
      return "Unlikely - Sold Too Long Ago";

    return "Yes";
  };

  const { watch, register, setValue, getValues } = useForm<IPartCancelRequest>({
    defaultValues: {
      cancelReason: "ADDRESS_ISSUES",
      partId: part.id!,
    },
  });

  const cancelReasonOptions = [
    { id: "ADDRESS_ISSUES", name: "Address Issues" },
    { id: "BUYER_ASKED_CANCEL", name: "Buyer Requested Cancellation" },
    { id: "ORDER_UNPAID", name: "Order Unpaid" },
    {
      id: "OUT_OF_STOCK_OR_CANNOT_FULFILL",
      name: "Out of Stock or Cannot Fulfill",
    },
    { id: "UNKNOWN", name: "Unknown" },
  ];

  const doCancel = () => {
    cancel.mutate(getValues(), {
      onSuccess: (d: any) => {
        console.log(d);
        setCancelOpen(false);
      },
    });
  };

  const handleGoToLead = () => {
    lead.mutate(part.vehicle!.id, {
      onSuccess: (lead) => {
        if (
          lead &&
          lead.data &&
          lead.data?.leads &&
          lead.data.leads.length > 0
        ) {
          openInNewTab(`/leads/${lead.data.leads[0].id}`);
        } else {
          addNotification({
            variant: "error",
            primaryText: "Could not find associated lead",
          });
        }
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to lookup lead",
        });
      },
    });
  };

  const currentPrice = useMemo(() => {
    //Sold price
    if (part.price) return part.price;

    //Has offers
    if (part.offers && part.offers.length > 0) {
      const offer = part.offers[0];
      return offer.format === FormatType.FixedPrice ? offer.buyItNowPrice : offer.auctionStartPrice;
    }

    //No offers yet
    if (part.enteredPrice) return part.enteredPrice;
  }, [part])

  return (
    <div className="flex flex-col gap-y-4">
      <Modal open={cancelOpen} setOpen={setCancelOpen} width="max-w-xl">
        <>
          <p className="text-xl font-semibold">Cancel Order</p>
          <div className="w-full flex flex-col gap-y-4 my-4">
            <p className="flex justify-between">
              <span>Title: </span>
              <span>{part.title}</span>
            </p>
            <p className="flex justify-between">
              <span>Tag: </span>
              <span>{part.tagNumber}</span>
            </p>
            <p className="flex justify-between">
              <span>Sold At: </span>
              <span>
                {formatDateTime(
                  part.soldDate,
                  DateTime.DATETIME_SHORT,
                  "Not Sold"
                )}
              </span>
            </p>
            <p className="flex justify-between">
              <span>Cancellation Acceptable: </span>
              <span>{getCancellable()}</span>
            </p>
            <SelectInput
              label="Reason for Cancellation"
              options={cancelReasonOptions}
              setValue={setValue}
              watch={watch}
              register={register}
              registerName={"cancelReason"}
            />
          </div>

          {confirmCancel ? (
            <div className="flex flex-row min-w-full gap-x-4">
              <ButtonInput
                label="Confirm"
                isSubmit={false}
                onClick={() => doCancel()}
                classes={"flex-grow"}
              />
              <ButtonInput
                label="Close"
                isSubmit={false}
                onClick={() => setConfirmCancel(false)}
                classes={"flex-grow"}
              />
            </div>
          ) : (
            <div className="flex flex-row min-w-full">
              <ButtonInput
                label="Cancel order"
                isSubmit={false}
                onClick={() => setConfirmCancel(true)}
                classes={"flex-grow"}
              />
            </div>
          )}
        </>
      </Modal>
      <SendToQcModal open={qcOpen} setOpen={setQcOpen} parts={[part]} />
      <LocationPicker open={open} setOpen={setOpen} part={part} />
      <Card title="Part Information" bodyClassName="p-4 space-y-4">
        <div>
          <p className="text-sm font-semibold">Part Title</p>
          <p>{part.title}</p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-semibold">Part Price</p>
            <p>{PriceFormat(currentPrice)}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Priced By</p>
            <p>
              {part.pricedBy ? <ContactFromId contactId={part.pricedBy} /> : "N/A"}
            </p>
          </div>

          <div>
            <p className="flex flex-row text-sm font-semibold">
              Location
              <FontAwesomeIcon
                className="rounded-full ml-1 cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setOpen(!open)}
              />
            </p>
            <WarehouseLocationFromId id={part.warehouseLocationId} />
          </div>
          <div>
            <p className="flex flex-row text-sm font-semibold">
              Located By
            </p>
            {locatedBy ? <p>{locatedBy}</p> : <p>-</p>}
          </div>
          <div>
            <p className="text-sm font-semibold">Tag Number</p>
            <p>{part.tagNumber ?? "Not Found"}</p>
          </div>
          <PartNumberEdit open={partNumberOpen} setOpen={setPartNumberOpen} part={part} />
          <div>
            <p className="flex flex-row text-sm font-semibold">
              Part Number
              <FontAwesomeIcon
                className="rounded-full ml-1 cursor-pointer transition-transform hover:scale-125"
                icon={faPencil}
                onClick={() => setPartNumberOpen(!partNumberOpen)}
              />
            </p>
            <p>{part.partNumber ?? "Not Found"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">OE Part Number</p>
            <p>{part.oePartNumber ?? "Not Found"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Published Date</p>
            <p>
              {formatDateTime(
                part.offers[0]?.publishedOn,
                DateTime.DATETIME_SHORT,
                "Not Published"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Listed Date</p>
            <p>
              {formatDateTime(
                part.offers[0]?.listingStarted,
                DateTime.DATETIME_SHORT,
                "No Listed Date"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Sold Date</p>
            <p>
              {formatDateTime(
                part.soldDate,
                DateTime.DATETIME_SHORT,
                "Not Sold"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Picked Date</p>
            <p>
              {formatDateTime(
                part.pickedOn,
                DateTime.DATETIME_SHORT,
                "Not Picked"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Shipped Date</p>
            <p>
              {formatDateTime(
                part.shippedOn,
                DateTime.DATETIME_SHORT,
                "Not Shipped"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Tracking Number</p>
            <p>{part.trackingNumber ?? "No Tracking Number"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Payment Status</p>
            <p>
              <EbayPaymentStatus offers={part.offers} />
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Payment Method</p>
            <p>
              <EbayPaymentMethod offers={part.offers} />
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Part Status</p>
            <p>{PartStatus[part.partStatus]}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Status Message</p>
            <p>{part.statusMessage ?? "-"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">QC Reason</p>
            <p className="whitespace-pre-line">{part.qcReason ?? "-"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">QC Comment</p>
            <p className="whitespace-pre-line">{part.qcApprovedComment ?? "-"}</p>
          </div>
          <div>
            <p className="text-sm font-semibold">Dismantled Date</p>
            <p>
              {formatDateTime(
                part.removedOn,
                DateTime.DATETIME_SHORT,
                "Not Dismantled"
              )}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Dismantled By</p>
            <p>
              {part.removedBy ? contact.data?.data?.fullname : "Not Dismantled"}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Est Picking</p>
            <p>
              {part.category?.pickingTime ? part.category.pickingTime + " mins" : "-"}
            </p>
          </div>
          <div>
            <p className="text-sm font-semibold">Est Packing</p>
            <p>
              {part.category?.packingTime ? part.category.packingTime + " mins" : "-"}
            </p>
          </div>
        </div>

        <div className="flex pt-4 justify-between items-center">

          <div className="flex flex-row gap-x-4">
            <ButtonInput
              label="Go to Listing Page"
              isSubmit={false}
              onClick={() => openInNewTab(`/ebay-listings/${part.id}`)}
              classes={undefined}
            />
            <ButtonInput
              label="Send to QC"
              isSubmit={false}
              onClick={() => setQcOpen(true)}
              classes={undefined}
            />
            {part.orderId != null ? (
              <ButtonInput
                label="Cancel order"
                isSubmit={false}
                onClick={() => setCancelOpen(true)}
                classes={undefined}
              />
            ) : null}
            <ButtonInput
              label="View Vehicle"
              isSubmit={false}
              onClick={() => openInNewTab("/vehicles/" + part.vehicle?.id)}
              classes={undefined}
            />
            <ButtonInput
              label="View Lead for Vehicle"
              isSubmit={false}
              onClick={handleGoToLead}
              classes={undefined}
            />
          </div>

          <div>
            {(part.ebayItemNumber || part.isPublishedOnShopify) ? (
              <ViewOnButton part={part} />
            ) : (
              <>
                {invoice.data?.data && (
                  <>
                    <CreditInvoiceModal
                      open={creditModalOpen}
                      setOpen={setCreditModalOpen}
                      invoice={invoice.data?.data ?? null}
                    />
                    <ButtonInput
                      label="Return Part"
                      isSubmit={false}
                      onClick={() => setCreditModalOpen(true)}
                      classes={"bg-red-500 hover:bg-red-600 mr-4"}
                    />
                    <ButtonInput
                      label="Download Invoice"
                      isSubmit={false}
                      onClick={() => {
                        downloadInvoice(invoice.data?.data.id!);
                      }}
                      classes={"!bg-emerald-500 hover:!bg-emerald-600"}
                    />
                  </>
                )}
              </>
            )}

            {invoice.data?.data && (
              <ButtonInput
                label="Download Invoice"
                isSubmit={false}
                onClick={() => downloadInvoice(invoice.data?.data.id!)}
                classes={"!bg-emerald-500 hover:!bg-emerald-600"}
              />
            )}
          </div>

        </div>
      </Card>

      {part.vehicle && (
        <PartVehicleDetails vehicle={part.vehicle!} price={null} />
      )}

      {part.partStatus === PartStatus.QC && (
        <PartQc part={part} />
      )}

      {/* <PartsRecall /> */}

      <VehiclePartsCard vehicleId={part.vehicleId!} />

      {/* Location, tag number, general part info, published date, listed date, sold date, picked date, shipped date, tracking info */}
    </div>
  );
};

const formatPartStatus = (status: PartStatus | null | undefined) => {
  const classes = "px-4 max-w-36 text-xs rounded-full h-10 flex justify-center items-center absolute top-[-78px] right-0";
  let colour = "bg-gray-200 text-gray-500";

  if (status === null || status === undefined) {
    return (
      <div className={`${classes} ${colour}`}>
        <p className="">N/A</p>
      </div>
    )
  }

  switch (status) {
    case PartStatus["On The Car"]:
      colour = "bg-orange-200 text-orange-500";
      break;
    case PartStatus.Damaged:
      colour = "bg-red-200 text-red-500";
      break;
    case PartStatus.Missing:
      colour = "bg-yellow-200 text-yellow-500";
      break;
    case PartStatus.Deleted:
      colour = "bg-gray-200 text-gray-500";
      break;
    case PartStatus.QC:
      colour = "bg-purple-200 text-purple-500";
      break;
    case PartStatus.Dismantled:
      colour = "bg-green-200 text-green-500";
      break;
    case PartStatus.Sold:
      colour = "bg-blue-200 text-blue-500";
      break;
    default:
      break;
  }

  return (
    <div className={`${classes} ${colour}`}>
      <p className="">{PartStatus[status]}</p>
    </div>
  )
};

export default function ViewStock() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { partId } = useParams();
  const { singlePart } = useSinglePart(partId!);
  const { deletePart, restorePart } = usePartMutations();
  const { deleteHandlers } = useDefaultCRUDHandlers("Part");
  const { addNotification } = useNotification();
  const { invoice } = usePartInvoice(partId!);

  const [part, setPart] = useState<IPart | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [viewType, setViewType] = useState(0);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const workOrderItems = useMemo<SearchPaletteItem[]>(() => {
    if (!part?.id) return [];
    const isSoldOrder = part.partStatus === PartStatus.Sold;
    return [
      {
        id: part.id,
        name: `${part.tagNumber ?? "Unknown Tag"} - ${part.title ?? "Part"}`,
        type: isSoldOrder ? "Order" : "Stock",
      },
    ];
  }, [part]);
  const viewTypes = [
    { value: 0, label: "Information" },
    { value: 1, label: "Images" },
    { value: 2, label: "Buyer Account" },
  ];

  useEffect(() => {
    setInfo({
      name: "Stock Details",
      desc: "View details of this part here",
    });
  }, []);

  useEffect(() => {
    if (!singlePart.isLoading && singlePart.data?.data) {
      let part = singlePart.data?.data;

      // console.log(part.vehicle?.parts);

      if (part?.offers && part?.offers.length > 0) {
        part.offers.sort(sortOffers);
      }

      if (part.images && part.images.length > 0) {
        part.images.sort((a, b) => {
          if (a.order === null) {
            return 1;
          }

          if (b.order === null) {
            return -1;
          }

          return a.order - b.order;
        });
      }

      setPart(part);
      setIsLoading(false);
      return;
    }

    if (!singlePart.isLoading) {
      setPart(undefined);
      setIsLoading(false);
    }
  }, [singlePart.isLoading, singlePart.data?.data, partId, isLoading]);

  if (isLoading) {
    return <LoadingWheel />;
  }

  if (!part) {
    return (
      <div className="mt-4">
        <Card title="Stock Details" bodyClassName="p-4">
          <p>Unable to load part details.</p>
        </Card>
      </div>
    );
  }

  const isDeletedPart = !!part?.deleted || part?.partStatus === PartStatus.Deleted;

  const handleRestorePart = () => {
    if (!part?.id) return;

    restorePart.mutate(part.id, {
      onSuccess: () => {
        addNotification({
          variant: "success",
          primaryText: "Part restored",
        });
        singlePart.refetch();
      },
      onError: () => {
        addNotification({
          variant: "error",
          primaryText: "Failed to restore part",
        });
      },
    });
  };

  const handleDeletePart = () => {
    if (!part?.id) return;

    deletePart.mutate(part.id, {
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        navigate("/stock");
      },
      onError: () => {
        deleteHandlers.onError();
      },
    });
  };

  return (
    <div className="mt-4 flex flex-col md:grid md:grid-cols-3 gap-6 relative">
      <ConfirmModal
        title="Delete Part?"
        message="Are you sure you want to delete this part?"
        confirmButtonText={null}
        onConfirm={handleDeletePart}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        isLoading={deletePart.isLoading}
      />
      {formatPartStatus(part?.partStatus)}
      {isDeletedPart && (
        <div className="col-span-3">
          <WarningBar
            button={
              <ButtonInput
                label="Restore Part"
                isSubmit={false}
                onClick={handleRestorePart}
                classes="bg-red-600 hover:bg-red-700"
                isLoading={restorePart.isLoading}
              />
            }
          >
            <div className="flex flex-col">
              <p className="text-sm font-semibold">This part is deleted</p>
              <p className="text-sm">Restore to put it back into stock workflows.</p>
            </div>
          </WarningBar>
        </div>
      )}
      <div className="col-span-3 flex flex-row overflow-x-auto">
        {part?.images
          .filter((x) => x.useOnEbay)
          .map((image) => (
            <img
              key={image.id}
              src={image.url}
              className="max-w-[10rem] m-1 border border-slate-500 rounded-md shadow-md"
            />
          ))}
      </div>

      {/* Left Column */}
      <div className={`col-span-2 flex flex-col`}>
        {/* Tabs */}
        <RadioGroup value={viewType} onChange={setViewType} className="my-4">
          <RadioGroup.Label className="sr-only">
            Choose which view to display
          </RadioGroup.Label>
          <div className="grid grid-cols-3 lg:grid-cols-3 gap-3">
            {viewTypes.map((type) => (
              <RadioGroup.Option
                key={`${type.value}`}
                value={type.value}
                className={({ active, checked }) =>
                  classNames(
                    active ? "ring-2 ring-offset-2 ring-primary-500" : "",
                    checked
                      ? "bg-primary-600 border-transparent text-white hover:bg-primary-700"
                      : "bg-white border-gray-200 text-gray-900 hover:bg-gray-50",
                    "cursor-pointer focus:outline-none border rounded-md py-1.5 px-3 flex items-center justify-center text-sm font-medium uppercase sm:flex-1"
                  )
                }
              >
                <RadioGroup.Label as="span">{type.label}</RadioGroup.Label>
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>

        <>
          {
            {
              0: <StockInformation part={part!} />,
              1: <EditListingImages part={part!} />,
              2: invoice?.data?.data?.account ? <AccountDetails account={invoice.data.data.account} /> : <>No linked account</>,
            }[viewType]
          }
        </>
      </div>

      {/* Right Column */}
      <div className="col-span-1 gap-6 flex flex-col mt-4">
        <LinkedWorkOrdersCard linkedItems={workOrderItems} />
        <StockPrintLabels part={part!} />
        <EbayPartMessages part={part!} />
        <Card title="Part Actions" bodyClassName="p-4">
          <div className="flex flex-col gap-4">
            <UnlistedDetailsCard fullPart={part!} />
            {!isDeletedPart && (
              <ButtonInput
                label="Delete Part"
                isSubmit={false}
                onClick={() => setDeleteOpen(true)}
                classes="!bg-red-600 hover:!bg-red-700"
              />
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
